import { yupResolver } from '@hookform/resolvers/yup';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { StatusDTO, useAchRelationshipsQuery } from '../../../../api/useAchRelationshipsQuery';
import { useValidateWithdrawMutation } from '../../../../api/useValidateWithdrawMutation';
import { MapTaxMode, TaxMode, WithdrawParams } from '../../../../api/useWithdrawMutation';
import {
  DistributionReasonDTO,
  DistributionWithholdingUnitDTO,
  StateWitholdingDetailsDTO,
} from '../../../../api/useWithdrawalOptionsQuery';
import { useTransferMoneyContext } from '../../../../hooks/useTransferMoneyContext';
import { AchRelationshipOption, FormValues, defaultValues, validation } from './WithdrawForm.meta';

export interface WithdrawMetadata {
  sourceAccount: string;
  destinationAccount: string;
  note: string;
}

interface UseWithdrawFormParams {
  isIraAccount?: boolean;
  distributionReason?: DistributionReasonDTO;
  stateWitholdingDetails: StateWitholdingDetailsDTO | null;
  onSuccess({ params, metadata }: { params: WithdrawParams; metadata: WithdrawMetadata }): void;
}

interface AsyncData {
  achRelationshipsOptions: AchRelationshipOption[];
}

export const useWithdrawForm = ({
  isIraAccount,
  distributionReason,
  stateWitholdingDetails,
  onSuccess,
}: UseWithdrawFormParams) => {
  const [withdrawImpediment, setWithdrawImpediment] = useState<{
    level: 'error' | 'warning';
    message: string;
  } | null>(null);

  const queryResult = useAchRelationshipsQuery();

  const { clientId, managedAccountId, accountName: sourceAccountName } = useTransferMoneyContext();

  const defaultStateTax =
    stateWitholdingDetails?.minimumAmount ||
    stateWitholdingDetails?.percentageRelativeToFederalTax ||
    0;

  const formBag = useForm<FormValues>({
    defaultValues: {
      ...defaultValues,
      stateTaxMode:
        stateWitholdingDetails?.amountType === DistributionWithholdingUnitDTO.Dollar
          ? TaxMode.Amount
          : TaxMode.Percentage,
      stateTax: defaultStateTax > 0 ? defaultStateTax.toString() : '',
    },
    context: {
      isIraAccount,
      stateWitholdingDetails,
    },
    resolver: yupResolver(validation),
  });

  const withdrawPreviewMutation = useValidateWithdrawMutation();

  const onSubmit = async (values: FormValues) => {
    setWithdrawImpediment(null);

    const amount = parseFloat(values.amount);
    const federalTax = values.federalTax !== '' ? parseFloat(values.federalTax) : null;
    const stateTax = values.stateTax !== '' ? parseFloat(values.stateTax) : null;

    const federalTaxWithholding =
      federalTax !== null
        ? { valueType: MapTaxMode[values.federalTaxMode], amountToWithhold: federalTax }
        : null;

    const stateTaxWithholding =
      stateTax !== null
        ? { valueType: MapTaxMode[values.stateTaxMode], amountToWithhold: stateTax }
        : null;

    const iraDistribution =
      isIraAccount && distributionReason
        ? {
            distributionReason: distributionReason.value,
            federalTaxWithholding,
            stateTaxWithholding,
          }
        : null;

    if (!values.toRelationship) {
      return;
    }

    try {
      const params = {
        clientId,
        fromManagedAccountId: managedAccountId,
        toRelationshipId: values.toRelationship.value,
        amount,
        iraDistribution,
      };

      const withdrawalValidation = await withdrawPreviewMutation.mutateAsync(params);

      if (!withdrawalValidation.canProcessTransfer) {
        setWithdrawImpediment({
          message: withdrawalValidation.rejectReason,
          level: withdrawalValidation.messageLevel.toLowerCase() as 'error' | 'warning',
        });
      } else {
        onSuccess({
          params,
          metadata: {
            note: withdrawalValidation.note,
            sourceAccount: sourceAccountName,
            destinationAccount: values.toRelationship.label,
          },
        });
      }
    } catch (err) {
      if (err instanceof Error) {
        setWithdrawImpediment({
          message: err.message,
          level: 'error',
        });
      }
    }
  };

  const asyncData: AsyncData | null = useMemo(() => {
    if (!queryResult.data) {
      return null;
    }

    const achRelationshipsOptions = queryResult.data
      .filter(({ status }) => status !== StatusDTO.REJECTED)
      .map(({ id, name }) => ({
        value: id,
        label: name,
      }));

    return {
      achRelationshipsOptions,
    };
  }, [queryResult.data]);

  return {
    ...queryResult,
    data: asyncData
      ? {
          asyncData,
          formBag,
          withdrawImpediment,
        }
      : null,
    actions: {
      onSubmit,
    },
  };
};
